/**
 * Viewport hook — single source of truth for JS breakpoint awareness.
 *
 * Breakpoints mirror Tailwind defaults so CSS and JS never disagree:
 *   phone   < 640px  (below `sm`)
 *   tablet  640–1023px (`sm` to below `lg`)
 *   desktop ≥ 1024px (`lg` and up)
 */

import { useSyncExternalStore } from 'react';

export const BREAKPOINT_SM = 640;
export const BREAKPOINT_LG = 1024;

export interface Viewport {
  /** below `sm` (640px) — iPhone portrait */
  isPhone: boolean;
  /** below `lg` (1024px) — phone or tablet; sidebar is a drawer here */
  isCompact: boolean;
  /** `lg` (1024px) and up — persistent sidebar */
  isDesktop: boolean;
}

const queries = {
  phone: `(max-width: ${BREAKPOINT_SM - 1}px)`,
  compact: `(max-width: ${BREAKPOINT_LG - 1}px)`,
};

function subscribe(callback: () => void): () => void {
  const lists = Object.values(queries).map(q => window.matchMedia(q));
  lists.forEach(l => l.addEventListener('change', callback));
  return () => lists.forEach(l => l.removeEventListener('change', callback));
}

let cached: Viewport = { isPhone: false, isCompact: false, isDesktop: true };

function getSnapshot(): Viewport {
  const isPhone = window.matchMedia(queries.phone).matches;
  const isCompact = window.matchMedia(queries.compact).matches;
  const isDesktop = !isCompact;
  if (
    cached.isPhone !== isPhone ||
    cached.isCompact !== isCompact ||
    cached.isDesktop !== isDesktop
  ) {
    cached = { isPhone, isCompact, isDesktop };
  }
  return cached;
}

export function useViewport(): Viewport {
  return useSyncExternalStore(subscribe, getSnapshot, () => cached);
}
